import {Flex, TextInput, Button, useMantineTheme, Menu, Checkbox, Stack, Badge, Switch, Divider} from "@mantine/core";
import {MaterialSymbol} from "react-material-symbols";
import checkboxClasses from "../../../styles/CheckboxShared.module.css";
import {ArticleMarketplaceStatus, MarketplaceName} from "../../../interfaces/response/MarketplaceStatus";
import {useMemo} from "react";

/**
 * Valori dei filtri disponibili
 */

export interface FilterValues {
    /** Marketplace selezionati (array vuoto = tutti) */
    marketplaces: MarketplaceName[];
    /** Stati selezionati (array vuoto = tutti) */
    statuses: string[];
    /** Categorie selezionate (array vuoto = tutte) */
    categories: string[];
    /** Gradi selezionati (array vuoto = tutti) */
    grades: string[];
    /** Disponibilità (undefined = tutte, true = disponibili, false = non disponibili) */
    availability?: boolean;
    /** Testo di ricerca (SKU, EAN, Titolo) */
    search: string;
    /** Mostra anche articoli disabilitati */
    showDisabled: boolean;
    /** Filtro avvertimenti: array di warning specifici (es. 'Titolo mancante', 'Prezzo non valido') */
    warnings: string[];
}

/**
 * Props per il componente MarketplaceFilters
 */

interface MarketplaceFiltersProps {
    /** Valori correnti dei filtri */
    filters: FilterValues;
    /** Callback per aggiornare i filtri */
    setFilters: (filters: FilterValues) => void;
    /** Callback per aggiornare i dati */
    onRefresh: () => void;
    /** Indica se è in corso un refresh */
    refreshing?: boolean;
    /** Dati per estrarre categorie disponibili */
    data?: ArticleMarketplaceStatus[];
}

/**
 * Componente con i filtri per la tabella marketplace.
 * Include:
 * - Ricerca testuale
 * - Filtro per marketplace
 * - Filtro per stato
 * - Pulsante refresh
 */

const MarketplaceFilters = ({filters, setFilters, onRefresh, refreshing, data}: MarketplaceFiltersProps) => {
    const theme = useMantineTheme();

    // Estrai categorie uniche dai dati
    const availableCategories = Array.from(new Set(data?.map(a => a.category) || []));

    const marketplaceOptions = [
        {value: 'EBAY', label: 'eBay'},
        {value: 'MEDIAWORLD', label: 'Mediaworld'},
        {value: 'LEROY_MERLIN', label: 'Leroy Merlin'},
        {value: 'KELKOO', label: 'Kelkoo'},
        {value: 'EPRICE', label: 'ePrice'}
    ];

    const statusOptions = [
        {value: 'enabled_not_in_feed', label: 'Abilitato ma non in feed'},
        {value: 'in_feed_not_published', label: 'In feed ma non pubblicato'},
        {value: 'published_not_sellable', label: 'Pubblicato ma non vendibile'},
        {value: 'enabled_not_sellable', label: 'Abilitato ma non vendibile'},
        {value: 'in_feed_but_suspended', label: 'In feed ma sospeso (Mirakl)'},
        {value: 'in_feed_but_invalid_data', label: 'In feed ma con dati invalidi (Mirakl)'},
        {value: 'fully_operational', label: 'Completamente operativo'}
    ];

    const handleMarketplaceChange = (marketplace: string, checked: boolean) => {
        const newMarketplaces = checked
            ? [...filters.marketplaces, marketplace as MarketplaceName]
            : filters.marketplaces.filter(m => m !== marketplace);
        setFilters({...filters, marketplaces: newMarketplaces});
    };

    const handleStatusChange = (status: string, checked: boolean) => {
        const newStatuses = checked
            ? [...filters.statuses, status]
            : filters.statuses.filter(s => s !== status);
        setFilters({...filters, statuses: newStatuses});
    };

    const handleCategoryChange = (category: string, checked: boolean) => {
        const newCategories = checked
            ? [...filters.categories, category]
            : filters.categories.filter(c => c !== category);
        setFilters({...filters, categories: newCategories});
    };

    const handleGradeChange = (grade: string, checked: boolean) => {
        const newGrades = checked
            ? [...filters.grades, grade]
            : filters.grades.filter(g => g !== grade);
        setFilters({...filters, grades: newGrades});
    };

    const handleAvailabilityChange = (value: string) => {
        if (value === 'all') {
            setFilters({...filters, availability: undefined});
        } else if (value === 'available') {
            setFilters({...filters, availability: true});
        } else if (value === 'unavailable') {
            setFilters({...filters, availability: false});
        }
    };

    const clearFilters = () => {
        setFilters({
            marketplaces: [],
            statuses: [],
            categories: [],
            grades: [],
            availability: undefined,
            search: '',
            showDisabled: false,
            warnings: []
        });
    };

    // Estrai gradi unici dai dati (escludendo undefined e null)
    const availableGrades = Array.from(new Set(data?.map(a => a.grade).filter((g): g is string => !!g) || []));

    // Estrai tutti i warning unici dai dati di tutti i marketplace
    const availableWarnings = useMemo(() => {
        if (!data) return [];
        const warningsSet = new Set<string>();
        data.forEach(article => {
            Object.values(article.marketplaceStatuses).forEach(status => {
                status?.publicationStatus?.warnings?.forEach(warning => {
                    if (warning) warningsSet.add(warning);
                });
            });
        });
        return Array.from(warningsSet).sort();
    }, [data]);

    const activeFiltersCount = filters.marketplaces.length + filters.statuses.length + filters.categories.length + filters.grades.length + (filters.availability !== undefined ? 1 : 0) + filters.warnings.length;

    // Handler per warnings
    const handleWarningsChange = (value: string, checked: boolean) => {
        const newWarnings = checked
            ? [...filters.warnings, value]
            : filters.warnings.filter(w => w !== value);
        setFilters({...filters, warnings: newWarnings});
    };

    // Seleziona/Deseleziona tutti i warnings
    const handleToggleAllWarnings = () => {
        if (filters.warnings.length === availableWarnings.length) {
            // Deseleziona tutti
            setFilters({...filters, warnings: []});
        } else {
            // Seleziona tutti
            setFilters({...filters, warnings: [...availableWarnings]});
        }
    };

    const allWarningsSelected = availableWarnings.length > 0 && filters.warnings.length === availableWarnings.length;
    const someWarningsSelected = filters.warnings.length > 0 && filters.warnings.length < availableWarnings.length;

    return (
        <Flex gap="sm" align="flex-end" wrap="wrap" mb="md">
            {/* Ricerca */}
            <TextInput
                placeholder="Cerca SKU, EAN, Titolo..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                leftSection={<MaterialSymbol icon="search" size={20}/>}
                radius="md"
                style={{width: 250}}
            />

            {/* Menu Marketplace */}
            <Menu shadow="md" width={200} closeOnItemClick={false}>
                <Menu.Target>
                    <Button
                        variant="outline"
                        color={theme.colors.textBlack?.[4] || 'gray'}
                        leftSection={<MaterialSymbol icon="storefront" size={20}/>}
                        rightSection={filters.marketplaces.length > 0 && <Badge size="sm" circle>{filters.marketplaces.length}</Badge>}
                        radius="md"
                        style={{minWidth: 160}}
                    >
                        Marketplace
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Stack gap="xs" p="xs">
                        {marketplaceOptions.map(option => (
                            <Checkbox
                                key={option.value}
                                label={option.label}
                                checked={filters.marketplaces.includes(option.value as MarketplaceName)}
                                onChange={(e) => handleMarketplaceChange(option.value, e.currentTarget.checked)}
                                size="sm"
                                radius="sm"
                                classNames={{ 
                                    input: checkboxClasses.checkboxInput, 
                                    icon: checkboxClasses.checkboxIcon, 
                                    inner: checkboxClasses.checkboxInner,
                                    label: checkboxClasses.checkboxLabel
                                }}
                            />
                        ))}
                    </Stack>
                </Menu.Dropdown>
            </Menu>

            {/* Menu Stato */}
            <Menu shadow="md" width={250} closeOnItemClick={false}>
                <Menu.Target>
                    <Button
                        variant="outline"
                        color={theme.colors.textBlack?.[4] || 'gray'}
                        leftSection={<MaterialSymbol icon="check_circle" size={20}/>}
                        rightSection={filters.statuses.length > 0 && <Badge size="sm" circle>{filters.statuses.length}</Badge>}
                        radius="md"
                        style={{minWidth: 130}}
                    >
                        Stato
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Stack gap="xs" p="xs">
                        {statusOptions.map(option => (
                            <Checkbox
                                key={option.value}
                                label={option.label}
                                checked={filters.statuses.includes(option.value)}
                                onChange={(e) => handleStatusChange(option.value, e.currentTarget.checked)}
                                size="sm"
                                radius="sm"
                                classNames={{ 
                                    input: checkboxClasses.checkboxInput, 
                                    icon: checkboxClasses.checkboxIcon, 
                                    inner: checkboxClasses.checkboxInner,
                                    label: checkboxClasses.checkboxLabel
                                }}
                            />
                        ))}
                    </Stack>
                </Menu.Dropdown>
            </Menu>

            {/* Menu Categoria */}
            {availableCategories.length > 0 && (
                <Menu shadow="md" width={220} closeOnItemClick={false}>
                    <Menu.Target>
                        <Button
                            variant="outline"
                            color={theme.colors.textBlack?.[4] || 'gray'}
                            leftSection={<MaterialSymbol icon="category" size={20}/>}
                            rightSection={filters.categories.length > 0 && <Badge size="sm" circle>{filters.categories.length}</Badge>}
                            radius="md"
                            style={{minWidth: 150}}
                        >
                            Categoria
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Stack gap="xs" p="xs" style={{maxHeight: 300, overflowY: 'auto'}}>
                            {availableCategories.map(category => (
                                <Checkbox
                                    key={category}
                                    label={category}
                                    checked={filters.categories.includes(category)}
                                    onChange={(e) => handleCategoryChange(category, e.currentTarget.checked)}
                                    size="sm"
                                    radius="sm"
                                    classNames={{ 
                                        input: checkboxClasses.checkboxInput, 
                                        icon: checkboxClasses.checkboxIcon, 
                                        inner: checkboxClasses.checkboxInner,
                                        label: checkboxClasses.checkboxLabel
                                    }}
                                />
                            ))}
                        </Stack>
                    </Menu.Dropdown>
                </Menu>
            )}

            {/* Menu Grado */}
            {availableGrades.length > 0 && (
                <Menu shadow="md" width={180} closeOnItemClick={false}>
                    <Menu.Target>
                        <Button
                            variant="outline"
                            color={theme.colors.textBlack?.[4] || 'gray'}
                            leftSection={<MaterialSymbol icon="grade" size={20}/>}
                            rightSection={filters.grades.length > 0 && <Badge size="sm" circle>{filters.grades.length}</Badge>}
                            radius="md"
                            style={{minWidth: 130}}
                        >
                            Grado
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Stack gap="xs" p="xs">
                            {availableGrades.map(grade => (
                                <Checkbox
                                    key={grade}
                                    label={grade}
                                    checked={filters.grades.includes(grade)}
                                    onChange={(e) => handleGradeChange(grade, e.currentTarget.checked)}
                                    size="sm"
                                    radius="sm"
                                    classNames={{ 
                                        input: checkboxClasses.checkboxInput, 
                                        icon: checkboxClasses.checkboxIcon, 
                                        inner: checkboxClasses.checkboxInner,
                                        label: checkboxClasses.checkboxLabel
                                    }}
                                />
                            ))}
                        </Stack>
                    </Menu.Dropdown>
                </Menu>
            )}

            {/* Menu Disponibilità */}
            <Menu shadow="md" width={200} closeOnItemClick={false}>
                <Menu.Target>
                    <Button
                        variant="outline"
                        color={theme.colors.textBlack?.[4] || 'gray'}
                        leftSection={<MaterialSymbol icon="inventory_2" size={20}/>}
                        rightSection={filters.availability !== undefined && <Badge size="sm" circle>1</Badge>}
                        radius="md"
                        style={{minWidth: 160}}
                    >
                        Disponibilità
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Stack gap="xs" p="xs">
                        <Checkbox
                            label="Tutte"
                            checked={filters.availability === undefined}
                            onChange={() => handleAvailabilityChange('all')}
                            size="sm"
                            radius="sm"
                            classNames={{ 
                                input: checkboxClasses.checkboxInput, 
                                icon: checkboxClasses.checkboxIcon, 
                                inner: checkboxClasses.checkboxInner,
                                label: checkboxClasses.checkboxLabel
                            }}
                        />
                        <Checkbox
                            label="Disponibile"
                            checked={filters.availability === true}
                            onChange={() => handleAvailabilityChange('available')}
                            size="sm"
                            radius="sm"
                            classNames={{ 
                                input: checkboxClasses.checkboxInput, 
                                icon: checkboxClasses.checkboxIcon, 
                                inner: checkboxClasses.checkboxInner,
                                label: checkboxClasses.checkboxLabel
                            }}
                        />
                        <Checkbox
                            label="Non disponibile"
                            checked={filters.availability === false}
                            onChange={() => handleAvailabilityChange('unavailable')}
                            size="sm"
                            radius="sm"
                            classNames={{ 
                                input: checkboxClasses.checkboxInput, 
                                icon: checkboxClasses.checkboxIcon, 
                                inner: checkboxClasses.checkboxInner,
                                label: checkboxClasses.checkboxLabel
                            }}
                        />
                    </Stack>
                </Menu.Dropdown>
            </Menu>

            {/* Menu Avvertimenti */}
            {availableWarnings.length > 0 && (
                <Menu shadow="md" width={280} closeOnItemClick={false}>
                    <Menu.Target>
                        <Button
                            variant="outline"
                            color={theme.colors.textBlack?.[4] || 'gray'}
                            leftSection={<MaterialSymbol icon="warning" size={20}/>}
                            rightSection={filters.warnings.length > 0 && <Badge size="sm" circle>{filters.warnings.length}</Badge>}
                            radius="md"
                            style={{minWidth: 170}}
                        >
                            Avvertimenti
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Stack gap="xs" p="xs">
                            <Checkbox
                                label={allWarningsSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                                checked={allWarningsSelected}
                                indeterminate={someWarningsSelected}
                                onChange={handleToggleAllWarnings}
                                size="sm"
                                radius="sm"
                                fw={500}
                                classNames={{ 
                                    input: checkboxClasses.checkboxInput, 
                                    icon: checkboxClasses.checkboxIcon, 
                                    inner: checkboxClasses.checkboxInner,
                                    label: checkboxClasses.checkboxLabel
                                }}
                            />
                            <Divider my={4} />
                            <Stack gap="xs" style={{maxHeight: 250, overflowY: 'auto'}}>
                                {availableWarnings.map(warning => (
                                    <Checkbox
                                        key={warning}
                                        label={warning}
                                        checked={filters.warnings.includes(warning)}
                                        onChange={(e) => handleWarningsChange(warning, e.currentTarget.checked)}
                                        size="sm"
                                        radius="sm"
                                        classNames={{ 
                                            input: checkboxClasses.checkboxInput, 
                                            icon: checkboxClasses.checkboxIcon, 
                                            inner: checkboxClasses.checkboxInner,
                                            label: checkboxClasses.checkboxLabel
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Menu.Dropdown>
                </Menu>
            )}

            {/* Switch Mostra Disabilitati */}
            <Switch
                label="Mostra disabilitati"
                checked={filters.showDisabled}
                onChange={(event) => setFilters({...filters, showDisabled: event.currentTarget.checked})}
                color="teal"
                size="sm"
            />

            {/* Pulsante Aggiorna */}
            <Button
                onClick={onRefresh}
                variant="outline"
                color={theme.colors.textBlack?.[4] || 'gray'}
                leftSection={<MaterialSymbol icon="refresh" size={20}/>}
                loading={refreshing}
                radius="md"
            >
                Aggiorna
            </Button>

            {/* Pulsante Pulisci Filtri */}
            {activeFiltersCount > 0 && (
                <Button
                    onClick={clearFilters}
                    variant="light"
                    color="red"
                    leftSection={<MaterialSymbol icon="filter_alt_off" size={20}/>}
                    radius="md"
                >
                    Pulisci ({activeFiltersCount})
                </Button>
            )}
        </Flex>
    );
};

export default MarketplaceFilters;
